import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Lock, AlertTriangle, ArrowLeft } from "lucide-react";
import { useConsoleAuth } from "@/lib/console-auth";
import "@/styles/console.css";

// Safe-redirect helper: only honour internal paths (open-redirect guard).
function safeReturnTo(loc) {
    try {
        const p = new URLSearchParams(loc.search).get("return");
        if (p && p.startsWith("/") && !p.startsWith("//")) return p;
    } catch {
        /* noop */
    }
    return "/mira/console";
}

export default function ConsoleLogin() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, user } = useConsoleAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // R-react-strict — auto-redirect lives in useEffect so it never runs during render.
    useEffect(() => {
        if (user) {
            navigate(safeReturnTo(location), { replace: true });
        }
    }, [user, navigate, location]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const r = await login(email.trim(), password);
        setSubmitting(false);
        if (r.ok) {
            navigate(safeReturnTo(location), { replace: true });
        } else {
            setError(r.error || "Authentication failed.");
        }
    };

    return (
        <div className="console-root flex items-center justify-center px-6 py-16" data-testid="console-login-page">
            <div className="w-full max-w-md">
                {/* Brass lock badge */}
                <div className="flex justify-center mb-8">
                    <div
                        className="w-20 h-20 rounded-2xl flex items-center justify-center"
                        style={{
                            background: "linear-gradient(135deg, #c9a96a 0%, #e0c082 100%)",
                            boxShadow: "0 8px 28px rgba(201, 169, 106, 0.25)",
                        }}
                    >
                        <Lock size={32} color="#0b1020" strokeWidth={2.5} />
                    </div>
                </div>

                <h1
                    className="text-center text-[28px] font-semibold tracking-tight"
                    style={{ fontFamily: "Georgia, serif" }}
                    data-testid="console-login-title"
                >
                    Concierge<sup style={{ fontSize: "0.5em", marginLeft: "1px" }}>®</sup> Console
                </h1>
                <p className="text-center mt-2" style={{ color: "var(--console-text-muted)", fontSize: 13 }}>
                    This area requires Concierge<sup style={{ fontSize: "0.6em" }}>®</sup> credentials
                </p>

                {/* Restricted area banner */}
                <div className="console-restricted-banner mt-8" data-testid="console-restricted-banner">
                    <AlertTriangle size={18} className="shrink-0 mt-[2px]" />
                    <div>
                        <p className="font-semibold mb-1" style={{ letterSpacing: "0.04em" }}>
                            Restricted Area
                        </p>
                        <p style={{ color: "rgba(255, 214, 107, 0.78)", fontSize: 12 }}>
                            Unauthorised access attempts are logged and monitored.
                        </p>
                    </div>
                </div>

                <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="console-login-form">
                    <div>
                        <label className="console-eyebrow block mb-2">Admin email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="console-input"
                            placeholder="you@lesconcierge.com"
                            autoComplete="email"
                            required
                            data-testid="console-login-email"
                        />
                    </div>

                    <div>
                        <label className="console-eyebrow block mb-2">Admin password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="console-input"
                            placeholder="••••••••"
                            autoComplete="current-password"
                            required
                            data-testid="console-login-password"
                        />
                    </div>

                    {error && (
                        <p
                            className="text-sm"
                            style={{ color: "var(--console-danger)" }}
                            data-testid="console-login-error"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="console-btn-primary w-full"
                        data-testid="console-login-submit"
                    >
                        {submitting ? "Accessing…" : "Access Console"}
                    </button>
                </form>

                <div className="mt-8 text-center">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm"
                        style={{ color: "var(--console-text-muted)" }}
                        data-testid="console-login-back"
                    >
                        <ArrowLeft size={14} />
                        Back to main site
                    </Link>
                </div>
            </div>
        </div>
    );
}
